import { Dispatch } from "redux";
import {
	addCurrency,
	deleteCurrency,
	getCurrencyConfig,
	updateExchangeRate
} from "@/Services/Settings";
import {
	AddCurrencyRequest,
	CurrencyConfigurationView,
	UpdateExchangeRateRequest
} from "@/Contracts";

export const CURRENCY_CONFIG_ACTIONS = {
	GET_REQUEST: "GET_CURRENCY_CONFIG_REQUEST",
	GET_SUCCESS: "GET_CURRENCY_CONFIG_SUCCESS",
	GET_FAIL: "GET_CURRENCY_CONFIG_FAIL",
	ADD_REQUEST: "ADD_CURRENCY_REQUEST",
	ADD_SUCCESS: "ADD_CURRENCY_SUCCESS",
	ADD_FAIL: "ADD_CURRENCY_FAIL",
	UPDATE_REQUEST: "UPDATE_EXCHANGE_RATE_REQUEST",
	UPDATE_SUCCESS: "UPDATE_EXCHANGE_RATE_SUCCESS",
	UPDATE_FAIL: "UPDATE_EXCHANGE_RATE_FAIL",
	DELETE_REQUEST: "DELETE_CURRENCY_REQUEST",
	DELETE_SUCCESS: "DELETE_CURRENCY_SUCCESS",
	DELETE_FAIL: "DELETE_CURRENCY_FAIL"
} as const;

export type CurrencyConfigAction =
	| { type: typeof CURRENCY_CONFIG_ACTIONS.GET_REQUEST }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.GET_SUCCESS; payload: CurrencyConfigurationView }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.GET_FAIL; error: string }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.ADD_REQUEST }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.ADD_SUCCESS; payload: CurrencyConfigurationView }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.ADD_FAIL; error: string }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.UPDATE_REQUEST }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.UPDATE_SUCCESS; payload: CurrencyConfigurationView }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.UPDATE_FAIL; error: string }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.DELETE_REQUEST }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.DELETE_SUCCESS; payload: { currency: string } }
	| { type: typeof CURRENCY_CONFIG_ACTIONS.DELETE_FAIL; error: string };

const errorMessage = (e: unknown): string =>
	e instanceof Error ? e.message : String(e);

export const fetchCurrencyConfig =
	() => async (dispatch: Dispatch<CurrencyConfigAction>) => {
		dispatch({ type: CURRENCY_CONFIG_ACTIONS.GET_REQUEST });
		try {
			const payload = await getCurrencyConfig();
			dispatch({ type: CURRENCY_CONFIG_ACTIONS.GET_SUCCESS, payload });
		} catch (e) {
			dispatch({
				type: CURRENCY_CONFIG_ACTIONS.GET_FAIL,
				error: errorMessage(e)
			});
		}
	};

export const addCurrencyAction =
	(payload: AddCurrencyRequest) =>
	async (dispatch: Dispatch<CurrencyConfigAction>) => {
		dispatch({ type: CURRENCY_CONFIG_ACTIONS.ADD_REQUEST });
		try {
			const updated = await addCurrency(payload);
			dispatch({
				type: CURRENCY_CONFIG_ACTIONS.ADD_SUCCESS,
				payload: updated
			});
			return updated;
		} catch (e) {
			dispatch({
				type: CURRENCY_CONFIG_ACTIONS.ADD_FAIL,
				error: errorMessage(e)
			});
			throw e;
		}
	};

export const updateExchangeRateAction =
	(code: string, payload: UpdateExchangeRateRequest) =>
	async (dispatch: Dispatch<CurrencyConfigAction>) => {
		dispatch({ type: CURRENCY_CONFIG_ACTIONS.UPDATE_REQUEST });
		try {
			const updated = await updateExchangeRate(code, payload);
			dispatch({
				type: CURRENCY_CONFIG_ACTIONS.UPDATE_SUCCESS,
				payload: updated
			});
			return updated;
		} catch (e) {
			dispatch({
				type: CURRENCY_CONFIG_ACTIONS.UPDATE_FAIL,
				error: errorMessage(e)
			});
			throw e;
		}
	};

export const deleteCurrencyAction =
	(code: string) =>
	async (dispatch: Dispatch<CurrencyConfigAction>) => {
		dispatch({ type: CURRENCY_CONFIG_ACTIONS.DELETE_REQUEST });
		try {
			await deleteCurrency(code);
			dispatch({
				type: CURRENCY_CONFIG_ACTIONS.DELETE_SUCCESS,
				payload: { currency: code }
			});
		} catch (e) {
			dispatch({
				type: CURRENCY_CONFIG_ACTIONS.DELETE_FAIL,
				error: errorMessage(e)
			});
			throw e;
		}
	};
